import { defineComponent as _defineComponent } from 'vue'
import { ref, withDefaults, watch } from '../../../adapter-vue';
import { IMessageModel, TUITranslateService } from '@tencentcloud/chat-uikit-engine';
import { TUIGlobal, getPlatform } from '@tencentcloud/universal-api';
import Icon from '../../common/Icon.vue';
import iconClose from '../../../assets/icon/icon-close.svg';
import iconArrowLeft from '../../../assets/icon/icon-arrow-left.svg';
import iconZoomIn from '../../../assets/icon/zoom-in.svg';
import iconZoomOut from '../../../assets/icon/zoom-out.svg';
import iconRotateLeft from '../../../assets/icon/rotate-left.svg';
import iconRotateRight from '../../../assets/icon/rotate-right.svg';
import iconDownload from '../../../assets/icon/download.svg';
import ImageItem from './image-item.vue';
import { Toast, TOAST_TYPE } from '../../common/Toast/index';
import { isPC, isMobile } from '../../../utils/env';


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    imageList: { default: () => [] },
    currentImage: { default: () => ({} as IMessageModel) }
  },
  emits: ['close'],
  setup(__props: any, { emit }) {

const props = __props as { imageList: IMessageModel[], currentImage: IMessageModel }



const imageFormatMap = new Map([
  [1, 'jpg'],
  [2, 'gif'],
  [3, 'png'],
  [4, 'bmp'],
]);



const zoom = ref(1);
const rotate = ref(0);
const currentImageIndex = ref(
  Math.max(
    0,
    props.imageList.findIndex((msg) => msg.ID === props.currentImage?.ID)
  )
);
const translateX = ref(0);
const translateY = ref(0);
const ulRef = ref();

// Touch state
const touchStore = {} as {
  pageX1?: number;
  pageY1?: number;
  pageX2?: number;
  pageY2?: number;
};
let isTwoTouch = false;
let isMoving = false;
let startX = 0;
let startY = 0;
let lastTranslateX = 0;
let lastTranslateY = 0;
let stopPropagationMouse = false

const isNumber = (value: any) => typeof value === 'number' && isFinite(value);

// 增加右键菜单弹出时的清理逻辑
const handleContextMenu = (e: Event) => {
  // 防止菜单弹出后图片“粘”在鼠标上
  e.preventDefault()
  e.stopPropagation()
};

const handleMouseDown = (e: MouseEvent) => {
  stopPropagationMouse = true
};
const handleMouseMove = (e: MouseEvent) => {
  if(stopPropagationMouse) {
    e.stopPropagation()
    stopPropagationMouse = false
  }
};
const handleMouseUp = (e: MouseEvent) => {
  stopPropagationMouse = false
};

// --- Touch handlers ---
const handleTouchStart = (e: TouchEvent) => {
  e.preventDefault();
  const touches = e.touches;
  if (touches.length >= 1) {
    startX = touches[0].pageX;
    startY = touches[0].pageY;
    lastTranslateX = translateX.value;
    lastTranslateY = translateY.value;
    isMoving = false;
  }
  if (touches.length === 2) {
    isTwoTouch = true;
    touchStore.pageX1 = touches[0].pageX;
    touchStore.pageY1 = touches[0].pageY;
    touchStore.pageX2 = touches[1].pageX;
    touchStore.pageY2 = touches[1].pageY;
  } else {
    isTwoTouch = false;
  }
};

const handleTouchMove = (e: TouchEvent) => {
  e.preventDefault();
  isMoving = true;
  const touches = e.touches;
  if (touches.length === 2 && isTwoTouch) {
    handleTwoTouches(e);
  } else if (touches.length === 1 && !isTwoTouch && (isPC || (isMobile && zoom.value !== 1))) {
    const dx = touches[0].pageX - startX;
    const dy = touches[0].pageY - startY;
    const { translateX: tx, translateY: ty } = getTranslatedOffset(dx, dy, rotate.value);
    translateX.value = lastTranslateX + tx / zoom.value;
    translateY.value = lastTranslateY + ty / zoom.value;
  }
};

const handleTouchEnd = (e: TouchEvent) => {
  e.preventDefault();
  if (isTwoTouch) {
    isTwoTouch = false;
    delete touchStore.pageX2;
    delete touchStore.pageY2;
    return;
  }
  // No swipe navigation for now (commented out as per original)
  // Only close on tap if not moved
  if (!isMoving && !isPC) {
    close();
  }
  if(isMoving && !isPC && zoom.value === 1) {
    const X = e?.changedTouches[0]?.pageX - startX;
    if(X > 100) {
      goPrev();
    } else if(X < -100) {
      goNext();
    }
  }
};

const handleTouchCancel = () => {
  isTwoTouch = false;
  touchStore.pageX1 = undefined;
  touchStore.pageY1 = undefined;
  touchStore.pageX2 = undefined;
  touchStore.pageY2 = undefined;
};

// --- Two-touch zoom ---
const handleTwoTouches = (e: TouchEvent) => {
  const touches = e.touches;
  if (touches.length < 2) return;

  const touch1 = touches[0];
  const touch2 = touches[1];

  if (!isNumber(touchStore.pageX2)) touchStore.pageX2 = touch2.pageX;
  if (!isNumber(touchStore.pageY2)) touchStore.pageY2 = touch2.pageY;

  if (
    !isNumber(touchStore.pageX1) ||
    !isNumber(touchStore.pageY1) ||
    !isNumber(touchStore.pageX2) ||
    !isNumber(touchStore.pageY2)
  ) {
    return;
  }

  const getDistance = (x1: number, y1: number, x2: number, y2: number) =>
    Math.hypot(x2 - x1, y2 - y1);

  const currentDist = getDistance(touch1.pageX, touch1.pageY, touch2.pageX, touch2.pageY);
  const initialDist = getDistance(
    touchStore.pageX1,
    touchStore.pageY1,
    touchStore.pageX2,
    touchStore.pageY2
  );

  if (initialDist <= 0) return;

  const touchZoom = currentDist / initialDist;
  const delta = touchZoom - 1;
  const damping = 0.25; // 调整此值控制缩放灵敏度
  const newZoom = zoom.value * (1 + delta * damping);

  // 统一限制范围（与按钮一致）
  zoom.value = Math.min(Math.max(0.125, newZoom), 4);
};

// --- Wheel zoom (PC) ---
const handleWheel = (e: WheelEvent) => {
  e.preventDefault();
  
  // 1. 排除不必要的干扰：如果正在横向滚动列表，不触发缩放
  if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

  // 2. 指数级缩放因子
  // 向上滚 deltaY 为负，zoom 应该变大；向下滚 deltaY 为正，zoom 应该变小
  const scaleSpeed = 0.1; // 调整这个值可以改变灵敏度
  const factor = Math.pow(1.1, -e.deltaY * scaleSpeed); 
  
  let newZoom = zoom.value * factor;

  // 3. 限制缩放范围
  // 0.125 (1/8倍) 到 8 (8倍)
  newZoom = Math.min(Math.max(0.125, newZoom), 4);

  // 4. 更新响应式数据
  zoom.value = newZoom;
};

// --- Transform utils ---
function getTranslatedOffset(dx: number, dy: number, rotation: number) {
  const r = ((rotation % 360) + 360) % 360;
  switch (r) {
    case 0: return { translateX: dx, translateY: dy };
    case 90: return { translateX: dy, translateY: -dx };
    case 180: return { translateX: -dx, translateY: -dy };
    case 270: return { translateX: -dy, translateY: dx };
    default:
      const rad = (rotation * Math.PI) / 180;
      return {
        translateX: dx * Math.cos(rad) - dy * Math.sin(rad),
        translateY: -dx * Math.sin(rad) - dy * Math.cos(rad),
      };
  }
}

// --- Actions ---
const zoomIn = () => (zoom.value = Math.min(zoom.value + 0.1, 4));
const zoomOut = () => (zoom.value = Math.max(zoom.value - 0.1, 0.125));
const rotateLeft = () => (rotate.value -= 90);
const rotateRight = () => (rotate.value += 90);
const close = () => emit('close');

const goNext = () => {
  if (currentImageIndex.value < props.imageList.length - 1) {
    currentImageIndex.value++;
    initStyle();
  }
};
const goPrev = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--;
    initStyle();
  }
};

const initStyle = () => {
  zoom.value = 1;
  rotate.value = 0;
  translateX.value = 0;
  translateY.value = 0;
};

// --- Image URL ---
const getImageUrl = (message: IMessageModel) => {
  return isPC
    ? message?.payload?.imageInfoArray[0]?.url
    : message?.payload?.imageInfoArray[2]?.url;
};

const save = () => {
  const imageMessage = props.imageList[
    currentImageIndex.value
  ] as IMessageModel;
  const imageSrc = imageMessage?.payload?.imageInfoArray[0]?.url;
  if (!imageSrc) {
    Toast({
      message: TUITranslateService.t('component.图片 url 不存在'),
      type: TOAST_TYPE.ERROR,
    });
    return;
  }
  switch (getPlatform()) {
    case 'wechat':
      // Get the user's current settings and get the album permissions
      TUIGlobal.getSetting({
        success: (res: any) => {
          if (!res?.authSetting['scope.writePhotosAlbum']) {
            TUIGlobal.authorize({
              scope: 'scope.writePhotosAlbum',
              success() {
                downloadImgInUni(imageSrc);
              },
              fail() {
                TUIGlobal.showModal({
                  title: '您已拒绝获取相册权限',
                  content: '是否进入权限管理，调整授权？',
                  success: (res: any) => {
                    if (res.confirm) {
                      // Call up the client applet settings interface and return the operation results set by the user.
                      // Ask the user to authorize again.
                      TUIGlobal.openSetting({
                        success: (res: any) => {
                          console.log(res.authSetting);
                        },
                      });
                    } else if (res.cancel) {
                      return Toast({
                        message: TUITranslateService.t('component.已取消'),
                        type: TOAST_TYPE.ERROR,
                      });
                    }
                  },
                });
              },
            });
          } else {
            // If you already have album permission, save directly to the album
            downloadImgInUni(imageSrc);
          }
        },
        fail: () => {
          Toast({
            message: TUITranslateService.t('component.获取权限失败'),
            type: TOAST_TYPE.ERROR,
          });
        },
      });
      break;
    case 'app':
      downloadImgInUni(imageSrc);
      break;
    default:
      downloadImgInWeb(imageSrc);
      break;
  }
};

const downloadImgInUni = (src: string) => {
  TUIGlobal.showLoading({
    title: '大图提取中',
  });
  TUIGlobal.downloadFile({
    url: src,
    success: function (res: any) {
      TUIGlobal.hideLoading();
      TUIGlobal.saveImageToPhotosAlbum({
        filePath: res.tempFilePath,
        success: () => {
          Toast({
            message: TUITranslateService.t('component.已保存至相册'),
            type: TOAST_TYPE.SUCCESS,
          });
        },
      });
    },
    fail: function () {
      TUIGlobal.hideLoading();
      Toast({
        message: TUITranslateService.t('component.图片下载失败'),
        type: TOAST_TYPE.ERROR,
      });
    },
  });
};

const downloadImgInWeb = (src: string) => {
  const option: any = {
    mode: 'cors',
    headers: new Headers({
      'Content-Type': 'application/x-www-form-urlencoded',
    }),
  };
  const imageMessage = props.imageList[
    currentImageIndex.value
  ] as IMessageModel;
  // 表示非腾讯地址的逻辑
  if(imageMessage?.payload?.imageFormat === undefined) {
    const a = document.createElement('a');
    a.href = src;
    a.target = '_blank';
    a.download = src;
    a.click();
    return;
  }
  
  const imageFormat: number = imageMessage?.payload?.imageFormat;
  if (!imageFormatMap.has(imageFormat)) {
    Toast({
      message: TUITranslateService.t('component.暂不支持下载此类型图片'),
      type: TOAST_TYPE.ERROR,
    });
    return;
  }
  // If the browser supports fetch, use blob to download, so as to avoid the browser clicking the a tag and jumping to the preview of the new page
  if ((window as any).fetch) {
    fetch(src, option)
      .then(res => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = url + '.' + imageFormatMap.get(imageFormat);
        a.click();
      });
  } else {
    const a = document.createElement('a');
    a.href = src;
    a.target = '_blank';
    a.download = src + '.' + imageFormatMap.get(imageFormat);
    a.click();
  }
};

// onUnmounted(() => {
//   document?.removeEventListener
//   && document?.removeEventListener('keydown', handleEsc);
// });
watch(
  () => props.currentImage,
  () => {
    initStyle();
    currentImageIndex.value = props.imageList.findIndex((message: any) => {
      return message.ID === props?.currentImage?.ID;
    });
  }, {
    immediate: true,
    deep: true
  })

return { __sfc: true,props, imageFormatMap, emit, zoom, rotate, currentImageIndex, translateX, translateY, ulRef, touchStore, isTwoTouch, isMoving, startX, startY, lastTranslateX, lastTranslateY, stopPropagationMouse, isNumber, handleContextMenu, handleMouseDown, handleMouseMove, handleMouseUp, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, handleTwoTouches, handleWheel, getTranslatedOffset, zoomIn, zoomOut, rotateLeft, rotateRight, close, goNext, goPrev, initStyle, getImageUrl, save, downloadImgInUni, downloadImgInWeb, Icon, iconClose, iconArrowLeft, iconZoomIn, iconZoomOut, iconRotateLeft, iconRotateRight, iconDownload, ImageItem, isPC, isMobile }
}

})